// @flow
import getRgbFromRad from './color-from-rad';
import { isEqual } from 'lodash/fp';

function rgb2Lab(rgb) {
    let r = rgb[0] / 255,
        g = rgb[1] / 255,
        b = rgb[2] / 255,
        x,
        y,
        z;

    r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
    g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
    b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

    x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
    y = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 1.0;
    z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;

    x = x > 0.008856 ? Math.pow(x, 1 / 3) : 7.787 * x + 16 / 116;
    y = y > 0.008856 ? Math.pow(y, 1 / 3) : 7.787 * y + 16 / 116;
    z = z > 0.008856 ? Math.pow(z, 1 / 3) : 7.787 * z + 16 / 116;

    return {
        L: 116 * y - 16,
        A: 500 * (x - y),
        B: 200 * (y - z)
    };
}

const square = base => Math.pow(base, 2);

function deltaE76(x, y) {
    return Math.sqrt(square(y.L - x.L) + square(y.A - x.A) + square(y.B - x.B));
}

const calculateDistance = (rgb1, rgb2) => {
    return deltaE76(rgb2Lab(rgb1), rgb2Lab(rgb2));
};

type ClosestMatch = {
    distance: number,
    rad?: number,
    rgb?: Uint8ClampedArray
};

type FindClosestMatch = (
    targetColor: Uint8ClampedArray,
    context: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number,
    containerWidth: number,
    handleRadiusPercent: number,
    pickerDepthPercent: number
) => ClosestMatch;

type InnerFindClosestMatch = (
    targetColor: Uint8ClampedArray,
    context: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number,
    containerWidth: number,
    handleRadiusPercent: number,
    pickerDepthPercent: number,
    rad?: number,
    ClosestMatch?: ClosestMatch
) => ClosestMatch;

const circle = Math.PI * 2;
const increment = 0.01;

const _findClosestMatch: InnerFindClosestMatch = (
    targetColor,
    context,
    canvasWidth,
    canvasHeight,
    containerWidth,
    handleRadiusPercent,
    pickerDepthPercent,
    rad = 0,
    closestMatch = { distance: Infinity }
) => {
    if (rad >= circle) {
        return closestMatch;
    }

    const rgb = getRgbFromRad(
        rad,
        context,
        canvasWidth,
        canvasHeight,
        containerWidth,
        handleRadiusPercent,
        pickerDepthPercent
    );

    if (isEqual(rgb, targetColor)) {
        return { distance: 0, rad, rgb };
    }

    const distance = calculateDistance(targetColor.slice(0, 3), rgb.slice(0, 3));

    return _findClosestMatch(
        targetColor,
        context,
        canvasWidth,
        canvasHeight,
        containerWidth,
        handleRadiusPercent,
        pickerDepthPercent,
        rad + increment,
        distance < closestMatch.distance ? { distance, rad, rgb } : closestMatch
    );
};

// This function finds radians of the closest matching colour on the color-picker
const findClosestMatch: FindClosestMatch = _findClosestMatch;

export default findClosestMatch;



// WEBPACK FOOTER //
// ./src/components/widgets/flipper/rgb-flipper/find-closest-match.js